import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { createStyles, makeStyles, type Theme } from "@material-ui/core/styles";
import type React from "react";
import { useState } from "react";
import { CreateScheduleWizard } from "../custom-schedule/CreateScheduleWizard";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiSelect-root": {
				backgroundColor: "#FFF",
			},
		},
		formControl: {
			width: "99%",
			marginTop: 8,
		},
		chips: {
			display: "flex",
			flexWrap: "wrap",
		},
		chip: {
			margin: 2,
		},
		noLabel: {
			marginTop: theme.spacing(3),
		},
		cronText: {
			fontSize: "0.75rem",
			color: "rgba(0, 0, 0, 0.54)",
			marginTop: "4px",
			marginLeft: "8px",
		},
	}),
);

const SCHEDULE_CODES = [
	{
		SchedulingId: 1,
		SchedulingNameCode: "None",
		SchedulePolicyCode: "N",
	},
	{
		SchedulingId: 2,
		SchedulingNameCode: "Shift",
		SchedulePolicyCode: "S",
	},
	{
		SchedulingId: 3,
		SchedulingNameCode: "Periodic",
		SchedulePolicyCode: "P",
	},
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

type Props = {
	value: string;
	onChange: Function;
	cronExpression?: string;
	onCronExpressionChange?: (expression: string) => void;
	isModalOpen?: boolean;
	setIsModalOpen?: (isOpen: boolean) => void;
	hideScheduleStart?: (hide: boolean) => void;
	onScheduleStartChange?: (date: Date) => void;
	scheduleStart?: Date | null;
};

export const ScheduleCodePicker = ({
	value,
	onChange,
	cronExpression,
	onCronExpressionChange,
	isModalOpen = false,
	setIsModalOpen,
	hideScheduleStart,
	onScheduleStartChange,
	scheduleStart,
}: Props) => {
	const classes = useStyles();
	const [localIsModalOpen, setLocalIsModalOpen] = useState(false);

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const newValue = event.target.value as string;
		onChange(newValue);

		if (newValue === "P") {
			if (setIsModalOpen) {
				setIsModalOpen(true);
			} else {
				setLocalIsModalOpen(true);
			}

			if (hideScheduleStart) {
				hideScheduleStart(true);
			}
		} else {
			if (hideScheduleStart) {
				hideScheduleStart(false);
			}
			if (onCronExpressionChange) {
				onCronExpressionChange("");
			}
		}
	};

	const handleWizardSave = (cronExp: string, scheduleDate: Date) => {
		if (onCronExpressionChange) {
			onCronExpressionChange(cronExp);
		}

		if (onScheduleStartChange) {
			onScheduleStartChange(scheduleDate);
		}

		if (setIsModalOpen) {
			setIsModalOpen(false);
		} else {
			setLocalIsModalOpen(false);
		}
	};

	const handleWizardClose = (shouldUpdate: boolean) => {
		if (!shouldUpdate && !cronExpression && value === "P") {
			const currentDay = new Date().getDate();
			const monthlyCronExp = `0 0 ${currentDay} * *`;

			if (onCronExpressionChange) {
				onCronExpressionChange(monthlyCronExp);
			}
		}

		if (setIsModalOpen) {
			setIsModalOpen(false);
		} else {
			setLocalIsModalOpen(false);
		}
	};

	const actualModalState = setIsModalOpen ? isModalOpen : localIsModalOpen;

	return (
		<div className={classes.root} style={{ position: "relative" }}>
			{SCHEDULE_CODES.length > 0 && (
				<FormControl
					variant="outlined"
					margin="dense"
					className={classes.formControl}
				>
					<InputLabel id="schedule-select-label">Schedule</InputLabel>
					<Select
						labelId="schedule-select-label"
						id="schedule-select"
						autoWidth={true}
						value={value}
						onChange={handleChange as any}
						label="Schedule"
						MenuProps={MenuProps}
					>
						{SCHEDULE_CODES.map((sc) => (
							<MenuItem
								key={`ScheduleId-${sc.SchedulingId}`}
								value={sc.SchedulePolicyCode}
							>
								<div style={{ display: "flex", alignItems: "center" }}>
									<div>{sc.SchedulingNameCode}</div>
								</div>
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}

			{value === "P" && (
				<CreateScheduleWizard
					show={actualModalState}
					onHide={handleWizardClose}
					onSave={handleWizardSave}
					initialCronExpression={cronExpression || ""}
					initialScheduleStart={scheduleStart}
				/>
			)}
		</div>
	);
};
